// קבצים מצורפים בשיחה (10 §1, 11 §1): צ'יפים לפני השליחה (שם, גודל, תמונה ממוזערת מקומית, הסרה, התקדמות העלאה), ורשימת
// הקבצים על הודעה שנשלחה — תמונות כתמונות ממוזערות (blob דרך ה-api, עם token; לא <img src=/api/…> כי אין cookie), שאר
// הקבצים ככרטיס שם+גודל; לחיצה מורידה. הקובץ לעולם לא מוצג inline כ-HTML/SVG — רק תמונות מוכרות, ורק כ-blob של תמונה.
// FileCard — קובץ שהסוכן שלח (אירוע file): כרטיס עם הורדה; תמונה בתצוגה, שמע/וידאו בנגן המקורי, PDF — "פתיחה" בלשונית חדשה.
// html/svg/אופיס — הורדה בלבד (לעולם לא נפתחים כ-blob באותו origin).
import { useEffect, useState } from 'react';
import { aiApi } from '../../lib/aiApi.ts';
import { buttonVariants } from '../ui/button.tsx';
import type { Attachment } from '../../lib/aiContract.ts';
import { T } from '../../lib/strings.ts';
import { cn } from '../../lib/utils.ts';

export type PendingFile = {
  key: string;
  file: File;
  /** object URL לתמונה (נוצר בבחירה, משוחרר בהסרה/שליחה). */
  preview: string | null;
  /** 0..1 בזמן העלאה; null לפני. */
  progress: number | null;
  errorHe: string | null;
};

const isImage = (mime: string) => /^image\/(png|jpeg|gif|webp)$/.test(mime);

/** blob של קובץ מצורף דרך ה-api; cache קטן לפי מזהה (reload של השיחה לא מוריד שוב). */
const blobCache = new Map<string, Promise<Blob>>();
export function useFileBlobUrl(conversationId: string | undefined, a: Attachment, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || !conversationId) return;
    let cancelled = false;
    let obj: string | null = null;
    const k = `${conversationId}:${a.id}`;
    let p = blobCache.get(k);
    if (!p) blobCache.set(k, (p = aiApi.fetchFile(conversationId, a.id)));
    p.then((b) => {
      if (cancelled) return;
      obj = URL.createObjectURL(b);
      setUrl(obj);
    }).catch(() => blobCache.delete(k));
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [conversationId, a.id, enabled]);
  return url;
}

export async function downloadAttachment(conversationId: string, a: Attachment) {
  const b = await aiApi.fetchFile(conversationId, a.id);
  const url = URL.createObjectURL(b);
  const el = document.createElement('a');
  el.href = url;
  el.download = a.name;
  document.body.appendChild(el);
  el.click();
  el.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function FileIcon({ mime, name, className }: { mime: string; name?: string; className?: string }) {
  const ext = name?.toLowerCase().match(/\.([a-z0-9]{1,4})$/)?.[1]?.toUpperCase();
  const tag = /^(audio|video|image)\//.test(mime) && ext ? ext : mime === 'application/pdf' ? 'PDF' : mime.includes('spreadsheet') || mime === 'application/vnd.ms-excel' ? 'XLS' : mime.includes('wordprocessing') ? 'DOC' : mime === 'text/csv' ? 'CSV' : mime === 'application/json' ? '{ }' : 'TXT';
  return (
    <span aria-hidden="true" className={cn('inline-flex size-9 shrink-0 items-center justify-center rounded-sm bg-muted font-mono text-[10px] font-bold text-muted-foreground', className)} dir="ltr">
      {tag}
    </span>
  );
}

/** הצ'יפים של הקבצים שטרם נשלחו (בתוך שדה הכתיבה). */
export function PendingChips({ files, onRemove, disabled }: { files: PendingFile[]; onRemove: (key: string) => void; disabled: boolean }) {
  if (!files.length) return null;
  return (
    <ul className="m-0 flex list-none flex-wrap gap-2 p-0" data-testid="pending-files" aria-label={T.ai.attach.files}>
      {files.map((f) => (
        <li
          key={f.key}
          className={cn('relative flex max-w-full items-center gap-2 rounded-sm border border-border bg-card p-1 pe-2 text-sm', f.errorHe && 'border-error/50')}
          data-testid="pending-file"
          data-name={f.file.name}
          data-state={f.errorHe ? 'error' : f.progress != null ? 'uploading' : 'ready'}
        >
          {f.preview ? <img src={f.preview} alt="" className="size-9 shrink-0 rounded object-cover" /> : <FileIcon mime={f.file.type} />}
          <span className="flex min-w-0 flex-col leading-tight">
            <bdi dir="auto" className="truncate font-medium">
              {f.file.name}
            </bdi>
            <span className={cn('text-xs', f.errorHe ? 'text-error' : 'text-muted-foreground')}>
              {f.errorHe ?? (f.progress != null ? T.ai.attach.uploading(Math.round(f.progress * 100)) : T.ai.attach.size(f.file.size))}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onRemove(f.key)}
            disabled={disabled}
            aria-label={T.ai.attach.remove(f.file.name)}
            className="ms-1 inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            data-testid="pending-file-remove"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
          {f.progress != null && (
            <span className="absolute inset-x-1 bottom-0 h-0.5 overflow-hidden rounded bg-muted" aria-hidden="true">
              <span className="block h-full bg-primary" style={{ width: `${Math.round(f.progress * 100)}%` }} />
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function SentFile({ conversationId, a }: { conversationId: string | undefined; a: Attachment }) {
  const image = isImage(a.mime);
  const url = useFileBlobUrl(conversationId, a, image);
  const [err, setErr] = useState<string | null>(null);
  const download = () => {
    if (!conversationId) return;
    setErr(null);
    downloadAttachment(conversationId, a).catch(() => setErr(T.ai.attach.downloadFailed));
  };
  return (
    <li data-testid="attachment" data-file-id={a.id} data-kind={image ? 'image' : 'file'} className="max-w-full">
      <button
        type="button"
        onClick={download}
        aria-label={T.ai.attach.download(a.name)}
        title={`${a.name} · ${T.ai.attach.size(a.bytes)}`}
        className={cn('flex max-w-full cursor-pointer items-center gap-2 rounded-sm border border-border bg-card text-start text-sm hover:border-primary/50', image ? 'p-0.5' : 'p-1 pe-3')}
      >
        {image ? (
          url ? (
            <img src={url} alt={a.name} className="max-h-48 min-h-10 max-w-full min-w-10 rounded object-contain" data-testid="attachment-thumb" />
          ) : (
            <span className="inline-block h-24 w-32 animate-pulse rounded bg-muted" aria-hidden="true" />
          )
        ) : (
          <>
            <FileIcon mime={a.mime} />
            <span className="flex min-w-0 flex-col leading-tight">
              <bdi dir="auto" className="truncate font-medium">
                {a.name}
              </bdi>
              <span className="text-xs text-muted-foreground">{T.ai.attach.size(a.bytes)}</span>
            </span>
          </>
        )}
      </button>
      {err && (
        <p role="alert" className="m-0 mt-1 text-xs text-error">
          {err}
        </p>
      )}
    </li>
  );
}

/** הקבצים של הודעה שנשלחה: תמונות ממוזערות + כרטיסים; לחיצה מורידה. */
export function AttachmentList({ conversationId, items }: { conversationId: string | undefined; items: Attachment[] }) {
  if (!items.length) return null;
  return (
    <ul className="m-0 flex list-none flex-wrap gap-2 p-0" data-testid="attachments" aria-label={T.ai.attach.files}>
      {items.map((a) => (
        <SentFile key={a.id} conversationId={conversationId} a={a} />
      ))}
    </ul>
  );
}

type FileKind = 'image' | 'pdf' | 'audio' | 'video' | 'file';
/** איך מציגים קובץ מהסוכן — רק סוגים שה-api מגיש inline; כל השאר (כולל svg/html) — הורדה בלבד. */
export function fileKind(mime: string): FileKind {
  if (isImage(mime)) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (/^audio\/(wav|mpeg|mp4|ogg)$/.test(mime)) return 'audio';
  if (/^video\/(mp4|webm)$/.test(mime)) return 'video';
  return 'file';
}

/** קובץ שהסוכן שלח למשתמש (10 §1 chat.sendFile / קבצי ריצת ניסיון): תצוגה לפי סוג + הורדה; PDF גם "פתיחה" בלשונית חדשה. */
export function FileCard({ conversationId, file, captionHe }: { conversationId: string | undefined; file: Attachment; captionHe: string | null }) {
  const kind = fileKind(file.mime);
  const url = useFileBlobUrl(conversationId, file, kind === 'image' || kind === 'audio' || kind === 'video');
  const [err, setErr] = useState<string | null>(null);
  const [mediaErr, setMediaErr] = useState(false);
  const download = () => {
    if (!conversationId) return;
    setErr(null);
    downloadAttachment(conversationId, file).catch(() => setErr(T.ai.attach.downloadFailed));
  };
  const open = () => {
    if (!conversationId) return;
    setErr(null);
    // הלשונית נפתחת בתוך הלחיצה (אחרת חוסם החלונות); ה-blob נטען אחר כך. נחסמה — הורדה במקום.
    const w = window.open('', '_blank');
    if (!w) return download();
    aiApi
      .fetchFile(conversationId, file.id)
      .then((b) => {
        const u = URL.createObjectURL(new Blob([b], { type: 'application/pdf' }));
        w.location.href = u;
        setTimeout(() => URL.revokeObjectURL(u), 60_000);
      })
      .catch(() => {
        w.close();
        setErr(T.ai.attach.downloadFailed);
      });
  };
  const btn = buttonVariants({ variant: 'outline', size: 'sm' });
  return (
    <div className="flex w-full max-w-md flex-col gap-2 rounded-md border border-border bg-card p-2" data-testid="file-card" data-file-id={file.id} data-kind={kind}>
      {kind === 'image' &&
        (url && !mediaErr ? (
          <button type="button" onClick={download} aria-label={T.ai.attach.download(file.name)} className="cursor-pointer self-start rounded">
            <img src={url} alt={file.name} onError={() => setMediaErr(true)} className="max-h-80 max-w-full rounded object-contain" data-testid="file-preview" />
          </button>
        ) : !mediaErr ? (
          <span className="inline-block h-32 w-full animate-pulse rounded bg-muted" aria-label={T.ai.attach.loading} />
        ) : null)}
      {kind === 'audio' && url && !mediaErr && <audio controls src={url} preload="metadata" onError={() => setMediaErr(true)} className="w-full" data-testid="file-audio" />}
      {kind === 'video' && url && !mediaErr && (
        <video controls playsInline src={url} preload="metadata" onError={() => setMediaErr(true)} className="max-h-80 w-full rounded bg-muted" data-testid="file-video" />
      )}
      {mediaErr && <p className="m-0 text-xs text-muted-foreground">{T.ai.attach.previewFailed}</p>}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <FileIcon mime={file.mime} name={file.name} />
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <bdi dir="auto" className="truncate font-medium" data-testid="file-name">
            {file.name}
          </bdi>
          <span className="text-xs text-muted-foreground">{T.ai.attach.size(file.bytes)}</span>
        </span>
        <span className="flex shrink-0 gap-2">
          {kind === 'pdf' && (
            <button type="button" onClick={open} aria-label={T.ai.attach.openLabel(file.name)} className={btn} data-testid="file-open">
              {T.ai.attach.open}
            </button>
          )}
          <button type="button" onClick={download} aria-label={T.ai.attach.download(file.name)} className={btn} data-testid="file-download">
            {T.ai.attach.downloadBtn}
          </button>
        </span>
      </div>
      {captionHe && (
        <p className="m-0 text-sm text-muted-foreground" data-testid="file-caption">
          {captionHe}
        </p>
      )}
      {err && (
        <p role="alert" className="m-0 text-xs text-error">
          {err}
        </p>
      )}
    </div>
  );
}
